import React, { ReactElement, useMemo } from 'react';
import classNames from 'classnames';
import { Button, BUTTON_STYLE_TYPE } from 'b2b-components/theme/components/Button';
import { Icon, ICON_NAME, ICON_SIZE } from 'b2b-components/theme/components/Icon';
import { upperCaseFirst } from 'b2b-apps/shop/common/utils/string/string';
import { ComponentType, Props } from './';
import 'b2b-apps/shop/common/components/InfoView/InfoView/InfoView.scss';

enum ICON_TYPE {
  error = 'error',
  info = 'info',
  success = 'success',
  warning = 'warning',
}

const InfoView: ComponentType = ({
  className,
  buttons,
  image,
  iconType,
  title,
  message,
  testId,
}: Props): ReactElement => {
  const imageSrcAttributes: {src?: string, srcSet?: string} = {};
  const rootClassName = classNames('InfoView', className);

  if (typeof image === 'string') {
    imageSrcAttributes.src = image;
  } else if (image.default && image.retina) {
    imageSrcAttributes.src = image.default;
    imageSrcAttributes.srcSet = `${image.retina} 2x`;
  }

  const renderedButtons = useMemo(
    (): ReactElement => (
      <>
        {buttons.secondary && (
          <Button
            onClick={buttons.secondary.onClick}
            styleType={BUTTON_STYLE_TYPE.secondary}
          >
            {buttons.secondary.text}
          </Button>
        )}
        {buttons.primary && (
          <Button
            onClick={buttons.primary.onClick}
            styleType={BUTTON_STYLE_TYPE.primary}
          >
            {buttons.primary.text}
          </Button>
        )}
      </>
    ),
    [buttons],
  );

  const renderedIcon = useMemo(
    (): ReactElement | null => {
      if (!iconType) {
        return null;
      }

      const iconName = `IconAlert${upperCaseFirst(iconType)}` as ICON_NAME;
      const iconClassName = classNames(
        'InfoView-TitleIcon',
        `InfoView-TitleIcon--${upperCaseFirst(iconType)}`,
      );

      return (
        <Icon name={iconName} size={ICON_SIZE.large} className={iconClassName} />
      );
    },
    [iconType],
  );

  return (
    <div data-testid={testId} className={rootClassName}>
      <div className='InfoView-ImageWrapper'>
        <img
          {...imageSrcAttributes}
          alt={title}
          title={title}
          className='InfoView-Image cds-ImgFluid'
        />
      </div>

      <div className='InfoView-Content'>
        <div className="InfoView-Title">
          {renderedIcon}
          <span className="InfoView-TitleText">{title}</span>
        </div>
        <div className="InfoView-MessageText">{message}</div>
        <div className="InfoView-ButtonGroup">{renderedButtons}</div>
      </div>
    </div>
  );
};

export {
  ICON_TYPE,
  InfoView,
};
